import { useCallback, useMemo, useState } from 'react'
import { usePlanMenuStore } from '../../../../../zustand/plan-menu/use-plan-menu-store'
import { useHistory, useParams } from 'react-router-dom'
import {
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import { tableColumnSizeOptions } from '../../../../../components/stories/organisms/table/constants'

import {
  planTreeSortByOptions,
  planTreeSortDirectionOptions,
  planTreeStateFilterOptions,
  useGetPlanTreeChildrenQueries,
  useGetPlanTreeQuery
} from '../../../../../react-query/plan/query/use-get-plan-tree-query'

import {
  buildColumnsDefault,
  buildColumnsSkeleton,
  buildData,
  buildDataSkeleton
} from '../table-options'
import {
  flattenRows,
  getExpandedFolderIds,
  menuTreeItemTypeOptions
} from '../../../../../components/menu-tree/utils'
import { usePlanMenuDnd } from './use-plan-menu-dnd'
import { trackFirebaseEvent } from '../../../../../firebase/firebase-analytics'
import { useGetProjectDetailsQuery } from '../../../../../react-query/project/query/use-get-project-details-query'

const emptyUserPermissions = []
const FOLDER_CHILDREN_PAGE_SIZE = 100

export const usePlanMenuTable = ({ formatMessage, locale }) => {
  const history = useHistory()
  const { projectId } = useParams()

  const { data: projectDetails } = useGetProjectDetailsQuery({
    projectId
  })

  const userPermissions = useMemo(
    () => projectDetails?.permissions ?? emptyUserPermissions,
    [projectDetails?.permissions]
  )

  const [pageNumber, setPageNumber] = useState(0)

  const pageSize = usePlanMenuStore((state) => state.planMenuPageSize)
  const setPageSize = usePlanMenuStore((state) => state.setPlanMenuPageSize)

  const search = usePlanMenuStore((state) => state.planMenuSearch)
  const setSearch = usePlanMenuStore((state) => state.setPlanMenuSearch)
  const showInactive = usePlanMenuStore((state) => state.planMenuShowInactive)
  const setShowInactive = usePlanMenuStore(
    (state) => state.setPlanMenuShowInactive
  )
  const [expanded, setExpanded] = useState({})
  const [rowSelection, setRowSelection] = useState({})
  const [sorting, setSorting] = useState([
    { id: planTreeSortByOptions.LAST_ACTIVITY, desc: true }
  ])

  const [
    { id: sortBy = planTreeSortByOptions.LAST_ACTIVITY, desc = true } = {}
  ] = sorting

  const sortDirection = desc
    ? planTreeSortDirectionOptions.DESC
    : planTreeSortDirectionOptions.ASC

  const stateFilter = showInactive
    ? planTreeStateFilterOptions.ALL
    : planTreeStateFilterOptions.ACTIVE

  const queryParams = useMemo(
    () => ({
      page: pageNumber,
      size: pageSize,
      sortBy,
      sortDirection,
      search,
      stateFilter
    }),
    [pageNumber, pageSize, search, sortBy, sortDirection, stateFilter]
  )

  const { data, isError, isLoading } = useGetPlanTreeQuery({
    projectId,
    params: queryParams
  })

  const expandedFolderIds = useMemo(
    () => getExpandedFolderIds(expanded),
    [expanded]
  )

  const { childrenByFolderId, folderChildrenStateById } =
    useGetPlanTreeChildrenQueries({
      projectId,
      folderIds: expandedFolderIds,
      params: {
        size: FOLDER_CHILDREN_PAGE_SIZE,
        sortBy,
        sortDirection,
        stateFilter
      }
    })

  const currentPage = data?.pages?.[0]

  const allData = useMemo(() => currentPage?.content ?? [], [currentPage])

  const tableData = useMemo(
    () => buildData(allData, { childrenByFolderId, locale, formatMessage }),
    [allData, childrenByFolderId, locale, formatMessage]
  )

  const rowsById = useMemo(
    () => new Map(flattenRows(tableData).map((row) => [row.id, row])),
    [tableData]
  )

  const hasFolderRow = useMemo(
    () =>
      Array.from(rowsById.values()).some(
        (row) => row.type === menuTreeItemTypeOptions.FOLDER
      ),
    [rowsById]
  )

  const selectedItems = useMemo(
    () =>
      Object.entries(rowSelection)
        .filter(([, isSelected]) => isSelected)
        .map(([rowId]) => rowsById.get(rowId))
        .filter(Boolean),
    [rowSelection, rowsById]
  )

  const clearRowSelection = useCallback(() => {
    setRowSelection({})
  }, [])

  const handleOpenPlan = useCallback(
    (planId) => {
      history.push(`/project/${projectId}/${planId}/plan`)
    },
    [history, projectId]
  )

  const resetPage = useCallback(() => {
    setPageNumber(0)
    setRowSelection({})
  }, [])

  const handlePageChange = useCallback((nextPageNumber) => {
    setPageNumber(Math.max(nextPageNumber, 0))
    setRowSelection({})
  }, [])

  const handlePageSizeChange = useCallback(
    (nextPageSize) => {
      setPageSize(nextPageSize)
      resetPage()
    },
    [resetPage]
  )

  const handleSearchChange = useCallback(
    (nextSearch) => {
      setSearch(nextSearch)
      resetPage()
    },
    [resetPage]
  )

  const handleShowInactiveChange = useCallback(
    (nextShowInactive) => {
      setShowInactive(nextShowInactive)
      resetPage()
    },
    [resetPage]
  )

  const handleSortingChange = useCallback(
    (updater) => {
      const nextSorting =
        typeof updater === 'function' ? updater(sorting) : updater

      if (nextSorting[0]?.id !== sorting[0]?.id) {
        trackFirebaseEvent('plan_list_sort_by_changed', {
          source: 'plan_menu',
          extra: nextSorting[0]?.id
        })
      }

      if (nextSorting[0]?.desc !== sorting[0]?.desc) {
        trackFirebaseEvent('plan_list_sort_direction_changed', {
          source: 'plan_menu',
          extra: nextSorting[0]?.desc
        })
      }

      setSorting(nextSorting)
      setPageNumber(0)
      setRowSelection({})
    },
    [sorting]
  )

  const columnsDefault = useMemo(
    () =>
      buildColumnsDefault({
        formatMessage,
        isSearching: !!search?.trim(),
        hasFolderRow,

        // plan handlers
        onOpenPlan: handleOpenPlan
      }),
    [formatMessage, handleOpenPlan, search, hasFolderRow]
  )

  const columnsSkeleton = useMemo(
    () =>
      buildColumnsSkeleton({
        formatMessage
      }),
    [formatMessage]
  )

  const dnd = usePlanMenuDnd({
    formatMessage,
    projectId,
    rowSelection,
    rowsById,
    setExpanded
  })

  const isTableSkeleton = isLoading
  const tableColumns = isTableSkeleton ? columnsSkeleton : columnsDefault

  const tableDataVariant = useMemo(
    () => (isTableSkeleton ? buildDataSkeleton(pageSize) : tableData),
    [isTableSkeleton, pageSize, tableData]
  )

  const table = useReactTable({
    data: tableDataVariant,
    columns: tableColumns,
    state: {
      sorting,
      expanded,
      rowSelection
    },
    defaultColumn: {
      size: 'auto',
      minSize: tableColumnSizeOptions.XXS
    },
    enableRowSelection: true,
    enableMultiSort: false,
    enableSortingRemoval: false,
    manualPagination: true,
    manualSorting: true,
    onExpandedChange: setExpanded,
    onRowSelectionChange: setRowSelection,
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowCanExpand: (row) =>
      !row.original.isSkeleton &&
      row.original.type === menuTreeItemTypeOptions.FOLDER,
    getSubRows: (row) => row.subRows,
    getRowId: (row) => row.id
  })

  const pagination = useMemo(
    () => ({
      pageNumber: currentPage?.number ?? pageNumber,
      pageSize: currentPage?.size ?? pageSize,
      totalElements: currentPage?.totalElements ?? 0,
      totalPages: currentPage?.totalPages ?? 0,
      onPageChange: handlePageChange
    }),
    [currentPage, handlePageChange, pageNumber, pageSize]
  )

  return {
    childrenByFolderId,
    dnd,
    folderChildrenStateById,
    isError,
    pagination,
    pageSize,
    rowSelection,
    rowsById,
    search,
    selectedItems,
    setPageSize: handlePageSizeChange,
    setSearch: handleSearchChange,
    setShowInactive: handleShowInactiveChange,
    showInactive,
    table,
    userPermissions,
    clearRowSelection,
    projectId
  }
}
